import * as XLSX from 'xlsx';

export const HORA_CERTA_REPORT_TITLE = 'Hora Certa | Relatórios | Logística';
export const INVALID_SPREADSHEET_MESSAGE =
  'A planilha selecionada não corresponde ao relatório Hora Certa esperado.';

export const HORA_CERTA_REQUIRED_HEADERS = [
  'Agendamento',
  'Armazéns',
  'Transportadora',
  'Tomador',
  'Tipo Operação',
  'Dt. Agendamento',
  'Peso do Agendamento (Kg)',
  'Motorista',
  'CPF',
  'Placa Veículo',
  'Tipo Veículo',
  'Operação',
];

export interface SpreadsheetSheet {
  name: string;
  columns: string[];
  rows: Array<Record<string, string | number | boolean | null>>;
}

export interface SpreadsheetPreview {
  fileName: string;
  fileSize: number;
  selectedSheetName: string;
  sheets: SpreadsheetSheet[];
}

const formatCell = (value: string | null | undefined): string => {
  const normalized = value == null ? '' : String(value).trim();
  return normalized ? `"${normalized}"` : '(vazio)';
};

const isBlank = (value: string | null | undefined): boolean =>
  value == null || String(value).trim() === '';

const MINIMUM_REQUIRED_HEADERS_FOR_DETECTION = 3;

const findHeaderRowIndex = (rows: Array<Array<string | null>>): number =>
  rows.findIndex((row) => {
    if (!Array.isArray(row)) return false;

    const presentHeaders = new Set(
      row
        .filter((header): header is string => typeof header === 'string')
        .map((header) => header.trim())
    );

    return (
      HORA_CERTA_REQUIRED_HEADERS.filter((header) => presentHeaders.has(header))
        .length >= MINIMUM_REQUIRED_HEADERS_FOR_DETECTION
    );
  });

function describeSpreadsheetMismatch(
  sheetName: string,
  title: string | null | undefined,
  missingHeaders: string[]
): string {
  const details: string[] = [`Aba "${sheetName}":`];

  if (!isBlank(title) && title !== HORA_CERTA_REPORT_TITLE) {
    details.push(
      `Linha 1 (título): esperado ${formatCell(
        HORA_CERTA_REPORT_TITLE
      )}, encontrado ${formatCell(title)}.`
    );
  }

  if (missingHeaders.length) {
    details.push(
      `Colunas obrigatórias ausentes: ${missingHeaders
        .map(formatCell)
        .join(', ')}.`
    );
  }

  return details.join('\n');
}

function validateHoraCertaSpreadsheet(
  worksheet: XLSX.WorkSheet,
  sheetName: string
): void {
  const rows = XLSX.utils.sheet_to_json<Array<string | null>>(worksheet, {
    header: 1,
    defval: null,
    raw: false,
    range: 0,
  });

  const headerRowIndex = findHeaderRowIndex(rows);
  const headerRow = headerRowIndex >= 0 ? rows[headerRowIndex] : [];
  const title = headerRowIndex > 0 ? (rows[0]?.[0] ?? null) : null;

  const presentHeaders = new Set(
    headerRow
      .filter((header): header is string => typeof header === 'string')
      .map((header) => header.trim())
  );
  const missingHeaders = HORA_CERTA_REQUIRED_HEADERS.filter(
    (header) => !presentHeaders.has(header)
  );
  const hasWrongTitle = !isBlank(title) && title !== HORA_CERTA_REPORT_TITLE;

  if (hasWrongTitle || missingHeaders.length) {
    throw new Error(
      `${INVALID_SPREADSHEET_MESSAGE}\n${describeSpreadsheetMismatch(
        sheetName,
        title,
        missingHeaders
      )}`
    );
  }
}

async function readFileBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export async function readSpreadsheet(file: File): Promise<SpreadsheetPreview> {
  const workbook = XLSX.read(await readFileBuffer(file), { type: 'array' });
  const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];

  if (!firstWorksheet) {
    throw new Error(INVALID_SPREADSHEET_MESSAGE);
  }

  validateHoraCertaSpreadsheet(firstWorksheet, workbook.SheetNames[0]);

  const sheets = workbook.SheetNames.map((name) => {
    const worksheet = workbook.Sheets[name];
    const headerRowIndex = findHeaderRowIndex(
      XLSX.utils.sheet_to_json<Array<string | null>>(worksheet, {
        header: 1,
        defval: null,
        raw: false,
        range: 0,
      })
    );
    const rows = XLSX.utils.sheet_to_json<
      Record<string, string | number | boolean | null>
    >(worksheet, {
      defval: null,
      range: headerRowIndex >= 0 ? headerRowIndex : 1,
    });

    return { name, columns: Object.keys(rows[0] || {}), rows };
  });

  return {
    fileName: file.name,
    fileSize: file.size,
    selectedSheetName: sheets[0]?.name || '',
    sheets,
  };
}
